import React, { useEffect, useRef, useState } from 'react';
import { useReveal } from '../hooks/useReveal';

export interface CountUpProps {
  /** Final value to count up to. */
  end: number;
  /** Animation duration in ms (design token: 800ms). */
  duration?: number;
  /** Number of decimal places to render. Default 0. */
  decimals?: number;
  /** Rendered before the number, e.g. "$" or "<". */
  prefix?: string;
  /** Rendered after the number, e.g. "%", "×", "s". */
  suffix?: string;
  /** Custom formatter — overrides decimals/locale formatting. */
  format?: (value: number) => string;
}

/** Expo-out easing — matches the design system entrance curve. */
const easeOutExpo = (t: number): number =>
  t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);

/**
 * Counts a number up from 0 when it scrolls into view (once, 20% viewport).
 *
 * - 800ms expo-out by default.
 * - prefers-reduced-motion: renders the final value instantly, no animation.
 */
const CountUp: React.FC<CountUpProps> = ({
  end,
  duration = 800,
  decimals = 0,
  prefix = '',
  suffix = '',
  format,
}) => {
  const { ref, revealed, reduced } = useReveal<HTMLSpanElement>();
  const [value, setValue] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!revealed) return undefined;

    if (reduced || typeof window.requestAnimationFrame !== 'function') {
      setValue(end);
      return undefined;
    }

    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(end * easeOutExpo(progress));
      if (progress < 1) {
        frameRef.current = window.requestAnimationFrame(tick);
      } else {
        setValue(end);
      }
    };

    frameRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [revealed, reduced, end, duration]);

  const formatted = format
    ? format(value)
    : value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

  return (
    <span ref={ref}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default CountUp;
